import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../AuthContext';
import {
    Card,
    CardBody,
    Typography,
    Button,
    Input,
    Dialog,
    DialogHeader,
    DialogBody,
    DialogFooter
} from '@material-tailwind/react';
import ReactSelect from 'react-select';
import { rootUrl } from '../../../config/config';
import OwnerTransactionPreviewDialog from './OwnerTransactionPreviewDialog';
import { FaEye, FaUndoAlt, FaFileInvoiceDollar, FaQuestionCircle, FaEdit, FaCheck, FaLock, FaUnlock, FaChartBar, FaPaperPlane } from 'react-icons/fa';
import { formatMoney } from '../shared/formatMoney';
import MonthlyInvoicesHeader from './monthly-invoices/MonthlyInvoicesHeader';
import {
    formatMonthStartDate,
    getInvoiceStatusMeta,
    isInvoiceEditable,
    normalizeInvoiceStatus
} from './monthly-invoices/helpers';

const MonthlyInvoices = () => {
    const { token, user } = useAuth();
    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth() + 1;
    const createPageStorageKey = 'monthlyInvoices.createSelections';
    const hasHydratedSelectionsRef = useRef(false);
    const hasHydratedOwnerSelectionRef = useRef(false);

    const monthOptions = useMemo(() => (
        Array.from({ length: 12 }, (_, index) => {
            const monthValue = index + 1;
            return {
                value: monthValue,
                label: new Date(2000, monthValue - 1).toLocaleString('default', { month: 'long' })
            };
        })
    ), []);

    const yearOptions = useMemo(() => (
        Array.from({ length: 5 }, (_, index) => {
            const yearValue = currentYear - 2 + index;
            return { value: yearValue, label: `${yearValue}` };
        })
    ), [currentYear]);

    const selectStyles = useMemo(() => ({
        control: (base) => ({
            ...base,
            borderColor: '#d1d5db',
            boxShadow: 'none',
            minHeight: '38px'
        }),
        menu: (base) => ({
            ...base,
            zIndex: 20
        })
    }), []);

    // State Management
    const [activeTab, setActiveTab] = useState('create');
    const [monthlySettings, setMonthlySettings] = useState(null);
    const [ownerOptions, setOwnerOptions] = useState([]);
    const [ownerRecords, setOwnerRecords] = useState([]);
    const [ownersLoaded, setOwnersLoaded] = useState(false);
    const [selectedOwner, setSelectedOwner] = useState(null);
    const [selectedYear, setSelectedYear] = useState(null);
    const [selectedMonth, setSelectedMonth] = useState(null);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [postLoading, setPostLoading] = useState(false);
    const [previewData, setPreviewData] = useState(null);
    const [previewRows, setPreviewRows] = useState([]);
    const [previewError, setPreviewError] = useState('');
    const [previewErrorLink, setPreviewErrorLink] = useState(null);
    const [previewSignature, setPreviewSignature] = useState('');
    const [postResult, setPostResult] = useState(null);
    const [invoiceDate, setInvoiceDate] = useState(() => formatMonthStartDate(currentYear, currentMonth));
    const [notes, setNotes] = useState('');
    const [recentInvoices, setRecentInvoices] = useState([]);
    const [invoicesLoading, setInvoicesLoading] = useState(false);
    const [reversingInvoiceId, setReversingInvoiceId] = useState(null);
    const [reverseError, setReverseError] = useState('');
    const [reverseSuccess, setReverseSuccess] = useState(null);
    const [permissions, setPermissions] = useState([]);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [editingInvoice, setEditingInvoice] = useState(null);
    const [editingInvoiceDate, setEditingInvoiceDate] = useState('');
    const [editingInvoiceNotes, setEditingInvoiceNotes] = useState('');
    const [editingInvoiceBusy, setEditingInvoiceBusy] = useState(false);
    const [invoiceActionBusy, setInvoiceActionBusy] = useState('');
    const [invoiceActionError, setInvoiceActionError] = useState('');
    const [invoiceActionMessage, setInvoiceActionMessage] = useState('');
    const [bulkSendingDraftInvoices, setBulkSendingDraftInvoices] = useState(false);
    const [selectedDraftInvoiceIds, setSelectedDraftInvoiceIds] = useState([]);
    const [previewTransaction, setPreviewTransaction] = useState(null);
    const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
    const previewErrorSectionRef = useRef(null);
    const getApiErrorMessage = useCallback((err, fallbackMessage) => (
        err?.response?.data?.message
        || err?.response?.data?.error
        || err?.message
        || fallbackMessage
    ), []);
    const [errors, setErrors] = useState({
        owner: '',
        year: '',
        month: ''
    });
    const currentDaysInMonth = useMemo(() => {
        if (!previewData?.days_in_month) {
            return 31;
        }
        return previewData.days_in_month;
    }, [previewData]);
    const currentPreviewSignature = useMemo(() => (
        [
            selectedOwner?.value || '',
            selectedYear?.value || '',
            selectedMonth?.value || '',
            invoiceDate || ''
        ].join('|')
    ), [invoiceDate, selectedMonth?.value, selectedOwner?.value, selectedYear?.value]);

    const compactAmountContainerClass = '!w-[96px] !min-w-0 mx-auto';
    const compactDaysContainerClass = '!w-[72px] !min-w-0 mx-auto';
    const compactInputClass = '!mt-0 !min-w-0 text-xs !px-2 !py-1';
    const compactNumberInputProps = {
        type: 'number',
        step: '0.01',
        className: `${compactInputClass} w-full text-right`
    };
    const actionIconBaseClass = 'inline-flex h-9 w-9 items-center justify-center rounded-full border shadow-sm transition disabled:opacity-60 disabled:cursor-not-allowed';
    const actionIconPreviewClass = `${actionIconBaseClass} border-slate-200 bg-white text-slate-700 hover:bg-slate-50`;
    const actionIconEditClass = `${actionIconBaseClass} border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100`;
    const actionIconSentClass = `${actionIconBaseClass} border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100`;
    const actionIconPaidClass = `${actionIconBaseClass} border-green-200 bg-green-50 text-green-700 hover:bg-green-100`;
    const actionIconUnlockClass = `${actionIconBaseClass} border-gray-200 bg-gray-100 text-gray-700 hover:bg-gray-200`;
    const actionIconReverseClass = `${actionIconBaseClass} border-red-200 bg-red-50 text-red-700 hover:bg-red-100`;
    const canManageMonthlyInvoices = permissions.includes('owner_invoice.edit') || permissions.includes('roles.manage');
    const navigate = useNavigate();

    const getInvoiceSelectionKey = useCallback((invoice) => String(invoice?.id || '').trim(), []);

    const getInvoiceActionError = useCallback((message, invoice) => {
        const statusMeta = getInvoiceStatusMeta(invoice);
        setInvoiceActionError(message || `This invoice is ${statusMeta.status.toLowerCase()} and cannot be edited.`);
    }, []);

    const readPersistedCreateSelections = useCallback(() => {
        if (typeof window === 'undefined') {
            return {};
        }

        try {
            const rawValue = window.localStorage.getItem(createPageStorageKey);
            if (!rawValue) {
                return {};
            }

            const parsed = JSON.parse(rawValue);
            return parsed && typeof parsed === 'object' ? parsed : {};
        } catch (err) {
            console.warn('Unable to read monthly invoice selections from storage:', err);
            return {};
        }
    }, [createPageStorageKey]);

    const persistCreateSelections = useCallback((nextSelections) => {
        if (typeof window === 'undefined') {
            return;
        }

        try {
            window.localStorage.setItem(createPageStorageKey, JSON.stringify(nextSelections));
        } catch (err) {
            console.warn('Unable to persist monthly invoice selections:', err);
        }
    }, [createPageStorageKey]);

    const normalizePreviewMoney = (value) => {
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : 0;
    };

    const buildPreviewRow = (car, index) => {
        const baseAmount = normalizePreviewMoney(car.base_amount);
        const grossAmount = normalizePreviewMoney(car.gross_amount ?? baseAmount);
        const hasPayloadDiscount = car.discount_amount !== null && car.discount_amount !== undefined && String(car.discount_amount).trim() !== '' && Number(car.discount_amount) > 0;
        const discountFromPayload = hasPayloadDiscount ? normalizePreviewMoney(car.discount_amount) : 0;
        const inferredDiscount = Math.max(0, grossAmount - normalizePreviewMoney(car.prorated_amount));
        const discountAmount = Math.max(0, Math.min(
            hasPayloadDiscount
                ? discountFromPayload
                : (inferredDiscount > 0 ? inferredDiscount : (car.is_discounted ? grossAmount : 0)),
            grossAmount
        ));
        const proratedAmount = Math.max(0, normalizePreviewMoney(car.prorated_amount ?? (grossAmount - discountAmount)));

        return {
            id: `${car.plate_no}-${index}`,
            plate_no: car.plate_no,
            allocation_date: car.allocation_date,
            base_amount: baseAmount,
            days_counted: car.days_counted,
            gross_amount: grossAmount,
            discount_amount: discountAmount,
            prorated_amount: proratedAmount,
            line_item_count: Math.max(1, parseInt(car.line_item_count, 10) || 1),
            invoice_year: car.invoice_year,
            invoice_month: car.invoice_month,
            invoice_period: car.invoice_period,
            _prorated_for_period: car._prorated_for_period || car.invoice_period || null,
            include: true
        };
    };

    // Fetch monthly invoice settings
    const fetchMonthlySettings = useCallback(async () => {
        try {
            const response = await axios.get(`${rootUrl}/system/owner_monthly_invoice_amount`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.data && response.data.value) {
                setMonthlySettings(parseFloat(response.data.value));
            }
        } catch (err) {
            console.error('Error fetching monthly settings:', err);
        }
    }, [token]);

    const fetchOwners = useCallback(async () => {
        try {
            const response = await axios.get(`${rootUrl}/owners`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            const ownerData = Array.isArray(response.data)
                ? response.data
                : (response.data && response.data.data) ? response.data.data : [];
            setOwnerRecords(ownerData);
            const invoiceableOwners = ownerData.filter(owner => owner.is_invoiced);
            const options = invoiceableOwners.map(owner => ({
                value: owner.code,
                label: `${owner.code} - ${owner.name}`
            }));
            setOwnerOptions(options);
        } catch (err) {
            console.error('Error fetching owners:', err);
        } finally {
            setOwnersLoaded(true);
        }
    }, [token]);

    // Load settings and owners on mount
    useEffect(() => {
        fetchMonthlySettings();
        fetchOwners();
    }, [fetchMonthlySettings, fetchOwners]);

    useEffect(() => {
        let active = true;

        const fetchPermissions = async () => {
            try {
                const response = await axios.get(`${rootUrl}/api/users/me/permissions`, {
                    headers: { Authorization: `Bearer ${token}` }
                });

                if (!active) {
                    return;
                }

                setPermissions(Array.isArray(response.data?.data) ? response.data.data : []);
            } catch (err) {
                if (active) {
                    console.error('Error fetching permissions:', err);
                    setPermissions([]);
                }
            }
        };

        if (token) {
            fetchPermissions();
        } else {
            setPermissions([]);
        }

        return () => {
            active = false;
        };
    }, [token]);

    useEffect(() => {
        if (hasHydratedSelectionsRef.current || !yearOptions.length || !monthOptions.length) {
            return;
        }

        const persistedSelections = readPersistedCreateSelections();
        const nextYear = persistedSelections.year
            ? yearOptions.find(option => option.value === Number(persistedSelections.year)) || yearOptions.find(option => option.value === currentYear)
            : yearOptions.find(option => option.value === currentYear);
        const nextMonth = persistedSelections.month
            ? monthOptions.find(option => option.value === Number(persistedSelections.month)) || monthOptions.find(option => option.value === currentMonth)
            : monthOptions.find(option => option.value === currentMonth);
        const nextTab = persistedSelections.activeTab === 'history' ? 'history' : 'create';

        if (nextYear) {
            setSelectedYear(nextYear);
        }
        if (nextMonth) {
            setSelectedMonth(nextMonth);
        }
        setActiveTab(nextTab);

        hasHydratedSelectionsRef.current = true;
    }, [currentMonth, currentYear, monthOptions, readPersistedCreateSelections, yearOptions]);

    useEffect(() => {
        if (!ownersLoaded || hasHydratedOwnerSelectionRef.current) {
            return;
        }

        const persistedSelections = readPersistedCreateSelections();
        if (persistedSelections.ownerCode) {
            const nextOwner = ownerOptions.find(option => option.value === persistedSelections.ownerCode) || null;
            if (nextOwner) {
                setSelectedOwner(nextOwner);
            }
        }
        hasHydratedOwnerSelectionRef.current = true;
    }, [ownerOptions, ownersLoaded, readPersistedCreateSelections]);

    useEffect(() => {
        if (selectedYear && selectedMonth) {
            setInvoiceDate(formatMonthStartDate(selectedYear.value, selectedMonth.value));
        }
    }, [selectedYear, selectedMonth]);

    useEffect(() => {
        if (!hasHydratedSelectionsRef.current || !hasHydratedOwnerSelectionRef.current) {
            return;
        }

        setPreviewData(null);
        setPreviewRows([]);
        setPostResult(null);
        setPreviewSignature('');
        setPreviewError('');
        setPreviewErrorLink(null);
    }, [selectedOwner?.value, selectedYear?.value, selectedMonth?.value, invoiceDate]);

    useEffect(() => {
        if (!hasHydratedSelectionsRef.current || !hasHydratedOwnerSelectionRef.current) {
            return;
        }

        persistCreateSelections({
            ownerCode: selectedOwner?.value || '',
            year: selectedYear?.value || '',
            month: selectedMonth?.value || '',
            activeTab
        });
    }, [activeTab, ownersLoaded, persistCreateSelections, selectedMonth?.value, selectedOwner?.value, selectedYear?.value]);

    const previewTotals = useMemo(() => {
        const includedRows = previewRows.filter(row => row.include === true);
        const totalAmount = includedRows.reduce((sum, row) => sum + (parseFloat(row.prorated_amount) || 0), 0);
        const totalGrossAmount = includedRows.reduce((sum, row) => sum + (parseFloat(row.gross_amount) || 0), 0);
        const totalDiscountAmount = includedRows.reduce((sum, row) => sum + (parseFloat(row.discount_amount) || 0), 0);
        return {
            carCount: includedRows.length,
            totalAmount,
            totalGrossAmount,
            totalDiscountAmount
        };
    }, [previewRows]);

    const previewPlateCounts = useMemo(() => {
        return previewRows.reduce((acc, row) => {
            const key = String(row.plate_no || '').trim().toUpperCase();
            if (!key) {
                return acc;
            }
            acc[key] = Math.max(acc[key] || 0, Math.max(1, parseInt(row.line_item_count, 10) || 1));
            return acc;
        }, {});
    }, [previewRows]);

    const getOwnerRecord = useCallback((ownerCode) => ownerRecords.find(owner => owner.code === ownerCode), [ownerRecords]);

    const buildPreviewTransaction = useCallback((invoice, fallbackOwnerCode = null) => {
        const ownerCode = invoice?.owner_code || fallbackOwnerCode || selectedOwner?.value || '';
        const owner = getOwnerRecord(ownerCode) || {};
        const docId = invoice?.invoice_ref || invoice?.id || `MOI-${ownerCode || 'OWNER'}`;
        const amount = parseFloat(invoice?.amount || invoice?.total_amount || invoice?.moi_amount || 0);
        const grossAmount = parseFloat(invoice?.gross_amount || amount || 0);
        const discountAmount = parseFloat(invoice?.discount_amount || 0);
        const detailLines = Array.isArray(invoice?.detail_lines)
            ? invoice.detail_lines
            : Array.isArray(invoice?.details)
                ? invoice.details
                : [];

        return {
            id: docId,
            owner_code: ownerCode,
            owner_name: invoice?.owner_name || owner.name || ownerCode,
            owner_email: owner.email || invoice?.owner_email || '',
            trans_type: 'MOI',
            short_desc: 'Monthly Invoice',
            date: invoice?.date || invoice?.invoice_date || new Date().toISOString().split('T')[0],
            amount,
            gross_amount: grossAmount,
            discount_amount: discountAmount,
            debit: amount,
            credit: 0,
            description: invoice?.description || `Monthly invoice for ${invoice?.car_count || invoice?.cars || previewTotals.carCount || 0} line items`,
            notes: invoice?.notes || '',
            running_balance: amount,
            car_plate_no: Array.isArray(invoice?.plates) ? invoice.plates.filter(Boolean).join(', ') : '',
            detail_lines: detailLines,
            reference_type: invoice?.reference_type || 'MONTHLY_INVOICE',
            reference_id: docId
        };
    }, [getOwnerRecord, previewTotals.carCount, selectedOwner?.value]);

    const buildOwnerTransactionPath = useCallback((ownerCode, transId) => (
        ownerCode && transId ? `/owners/transactions/${encodeURIComponent(ownerCode)}/${encodeURIComponent(transId)}` : null
    ), []);

    const getDocumentNumber = useCallback((value) => (
        String(value || '').split(':').pop() || value || '-'
    ), []);

    const setPreviewConflictMessage = useCallback((message, ownerCode, invoiceRef) => {
        const normalizedInvoiceRef = String(invoiceRef || '').trim();
        const invoiceNumber = normalizedInvoiceRef ? (normalizedInvoiceRef.split(':').pop() || normalizedInvoiceRef) : '';
        setPreviewError(message || '');
        setActiveTab('create');
        setPreviewErrorLink(
            ownerCode && normalizedInvoiceRef
                ? {
                    to: buildOwnerTransactionPath(ownerCode, normalizedInvoiceRef),
                    label: `Open Invoice #${invoiceNumber || normalizedInvoiceRef}`,
                    invoiceNumber,
                    invoiceRef: normalizedInvoiceRef
                }
                : null
        );
    }, [buildOwnerTransactionPath]);

    const openInvoicePreview = useCallback((invoice) => {
        setPreviewTransaction(buildPreviewTransaction(invoice));
        setPreviewDialogOpen(true);
    }, [buildPreviewTransaction]);

    const closeInvoicePreview = useCallback(() => {
        setPreviewDialogOpen(false);
        setPreviewTransaction(null);
    }, []);

    const updatePreviewRow = (rowId, updates) => {
        setPreviewRows(prevRows => prevRows.map((row) => {
            if (row.id !== rowId) {
                return row;
            }

            const nextRow = { ...row, ...updates };
            const baseAmount = parseFloat(nextRow.base_amount) || 0;
            const daysCounted = Math.max(0, parseInt(nextRow.days_counted, 10) || 0);
            const grossAmount = (Object.prototype.hasOwnProperty.call(updates, 'base_amount') || Object.prototype.hasOwnProperty.call(updates, 'days_counted'))
                ? ((baseAmount * daysCounted) / currentDaysInMonth)
                : (parseFloat(nextRow.gross_amount) || ((baseAmount * daysCounted) / currentDaysInMonth));
            const discountAmount = Math.max(0, Math.min(
                parseFloat(nextRow.discount_amount) || 0,
                grossAmount
            ));
            const proratedAmount = Math.max(0, grossAmount - discountAmount);

            return {
                ...nextRow,
                gross_amount: Number(grossAmount.toFixed(2)),
                discount_amount: Number(discountAmount.toFixed(2)),
                prorated_amount: Number(proratedAmount.toFixed(2)),
                line_item_count: Math.max(1, parseInt(nextRow.line_item_count, 10) || 1)
            };
        }));
    };

    const renderPlateLabel = (row) => {
        const plateCount = previewPlateCounts[String(row.plate_no || '').trim().toUpperCase()] || Math.max(1, parseInt(row.line_item_count, 10) || 1);

        return (
            <span className="inline-flex items-center gap-2">
                <span>{row.plate_no}</span>
                {plateCount > 1 && (
                    <span className="inline-flex items-center rounded-full bg-[#FFF3EE] px-2 py-0.5 text-[10px] font-bold text-[#FF5733]">
                        x{plateCount}
                    </span>
                )}
            </span>
        );
    };

    const handlePreview = async () => {
        let newErrors = {};
        if (!selectedOwner) newErrors.owner = 'Owner is required';
        if (!selectedYear) newErrors.year = 'Year is required';
        if (!selectedMonth) newErrors.month = 'Month is required';

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        setPreviewLoading(true);
        setPreviewError('');
        setPreviewErrorLink(null);
        setPostResult(null);

        try {
            const response = await axios.post(
                `${rootUrl}/api/job-cards/monthly-preview`,
                {
                    owner_code: selectedOwner.value,
                    year: selectedYear.value,
                    month: selectedMonth.value,
                    invoice_date: invoiceDate
                },
                {
                    headers: { Authorization: `Bearer ${token}` }
                }
            );

            if (response.data && response.data.success) {
                const data = response.data.data || {};
                const rows = (data.car_details || []).map(buildPreviewRow);
                const monthName = new Date(data.year, data.month - 1).toLocaleString('default', { month: 'short' });
                setPreviewData(data);
                setPreviewRows(rows);
                setPreviewSignature([
                    selectedOwner?.value || '',
                    selectedYear?.value || '',
                    selectedMonth?.value || '',
                    invoiceDate || ''
                ].join('|'));
                setNotes(`Monthly invoice for the month of ${monthName} ${data.year}`);
            } else {
                setPreviewData(null);
                setPreviewRows([]);
                setPreviewConflictMessage(
                    response.data?.message || 'Failed to preview invoice',
                    response.data?.data?.owner_code || selectedOwner?.value,
                    response.data?.data?.invoice_ref || response.data?.data?.trans_id
                );
            }
        } catch (err) {
            console.error('Error previewing monthly invoice:', err);
            setPreviewError(getApiErrorMessage(err, 'Failed to preview monthly invoice'));
            setPreviewErrorLink(null);
        } finally {
            setPreviewLoading(false);
        }
    };

    const handlePostInvoice = async () => {
        if (!previewData || previewRows.length === 0) {
            setPreviewError('Preview an invoice before posting');
            return;
        }

        if (previewSignature !== currentPreviewSignature) {
            setPreviewError('The selected owner, month, year, or invoice date changed. Preview the invoice again before posting.');
            return;
        }

        const includedRows = previewRows.filter((row) => row && row.include === true && String(row.plate_no || '').trim());
        if (includedRows.length === 0) {
            setPreviewError('Select at least one line item to post the invoice.');
            return;
        }

        setPostLoading(true);
        setPreviewError('');
        setPreviewErrorLink(null);

        try {
            const response = await axios.post(
                `${rootUrl}/api/job-cards/monthly-post`,
                {
                    owner_code: selectedOwner?.value,
                    year: selectedYear?.value,
                    month: selectedMonth?.value,
                    invoice_date: invoiceDate,
                    notes,
                items: includedRows.map(row => ({
                        include: true,
                        plate_no: row.plate_no,
                        base_amount: parseFloat(row.base_amount) || 0,
                        days_counted: parseInt(row.days_counted, 10) || 0,
                        gross_amount: parseFloat(row.gross_amount) || 0,
                        discount_amount: parseFloat(row.discount_amount) || 0,
                        prorated_amount: parseFloat(row.prorated_amount) || 0,
                        allocation_date: row.allocation_date,
                        invoice_year: row.invoice_year,
                        invoice_month: row.invoice_month,
                        invoice_period: row.invoice_period,
                        _prorated_for_period: row._prorated_for_period,
                        detail_invoice_period: row._prorated_for_period || row.invoice_period || `${selectedYear?.value}-${String(selectedMonth?.value).padStart(2, '0')}`
                    }))
                },
                {
                    headers: { Authorization: `Bearer ${token}` }
                }
            );

            if (response.data && response.data.success) {
                setPostResult(response.data.data);
            } else {
                setPreviewConflictMessage(
                    response.data?.message || 'Failed to post invoice',
                    response.data?.data?.owner_code || selectedOwner?.value,
                    response.data?.data?.invoice_ref || response.data?.data?.trans_id
                );
            }
        } catch (err) {
            console.error('Error posting monthly invoice:', err);
            setPreviewError(getApiErrorMessage(err, 'Failed to post monthly invoice'));
            setPreviewErrorLink(null);
        } finally {
            setPostLoading(false);
        }
    };

    const fetchRecentInvoices = useCallback(async (ownerCode) => {
        if (!ownerCode) {
            setRecentInvoices([]);
            return;
        }

        setInvoicesLoading(true);
        setReverseError('');

        try {
            const response = await axios.get(
                `${rootUrl}/api/job-cards/monthly-invoices/${ownerCode}`,
                {
                    headers: { Authorization: `Bearer ${token}` }
                }
            );

            if (response.data && response.data.success && Array.isArray(response.data.data)) {
                setRecentInvoices(response.data.data);
            } else {
                setRecentInvoices([]);
            }
        } catch (err) {
            console.error('Error fetching recent invoices:', err);
            setRecentInvoices([]);
        } finally {
            setInvoicesLoading(false);
        }
    }, [token]);

    const handleReverseInvoice = async (invoiceRef) => {
        if (!window.confirm(`Are you sure you want to reverse invoice ${invoiceRef}? This action cannot be undone.`)) {
            return;
        }

        setReversingInvoiceId(invoiceRef);
        setReverseError('');
        setReverseSuccess(null);

        try {
            const response = await axios.post(
                `${rootUrl}/api/job-cards/reverse-invoice`,
                {
                    invoice_ref: invoiceRef
                },
                {
                    headers: { Authorization: `Bearer ${token}` }
                }
            );

            if (response.data && response.data.success) {
                setReverseSuccess(response.data.data);
                // Refresh the invoice list
                if (selectedOwner) {
                    await fetchRecentInvoices(selectedOwner.value);
                }
            } else {
                setReverseError(response.data?.message || 'Failed to reverse invoice');
            }
        } catch (err) {
            console.error('Error reversing invoice:', err);
            setReverseError('Failed to reverse invoice');
        } finally {
            setReversingInvoiceId(null);
        }
    };

    const refreshInvoiceHistory = useCallback(async () => {
        if (selectedOwner?.value) {
            await fetchRecentInvoices(selectedOwner.value);
        }
    }, [fetchRecentInvoices, selectedOwner?.value]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const openEditInvoiceDialog = useCallback((invoice) => {
        if (!invoice) {
            return;
        }

        if (!isInvoiceEditable(invoice)) {
            getInvoiceActionError('Only draft invoices can be edited.', invoice);
            return;
        }

        setInvoiceActionError('');
        navigate(`/owners/invoices/monthly/edit/${encodeURIComponent(invoice.owner_code)}/${encodeURIComponent(invoice.id)}`);
    }, [getInvoiceActionError, isInvoiceEditable, navigate]);

    const closeEditInvoiceDialog = useCallback(() => {
        setEditDialogOpen(false);
        setEditingInvoice(null);
        setEditingInvoiceDate('');
        setEditingInvoiceNotes('');
        setEditingInvoiceBusy(false);
    }, []);

    const saveEditedInvoice = async () => {
        if (!editingInvoice) {
            return;
        }

        setEditingInvoiceBusy(true);
        setInvoiceActionError('');

        try {
            const response = await axios.put(
                `${rootUrl}/api/owners/${encodeURIComponent(editingInvoice.owner_code)}/transactions/${encodeURIComponent(editingInvoice.id)}/monthly-invoice`,
                {
                    invoice_date: editingInvoiceDate,
                    notes: editingInvoiceNotes,
                    updated_by: user?.username || user?.name || 'system'
                },
                {
                    headers: { Authorization: `Bearer ${token}` }
                }
            );

            if (response.data?.success) {
                closeEditInvoiceDialog();
                await refreshInvoiceHistory();
                navigate(buildOwnerTransactionPath(editingInvoice.owner_code, editingInvoice.id), { replace: true });
            } else {
                setInvoiceActionError(response.data?.message || 'Failed to update invoice');
            }
        } catch (err) {
            console.error('Error updating monthly invoice:', err);
            setInvoiceActionError(err.response?.data?.message || 'Failed to update invoice');
        } finally {
            setEditingInvoiceBusy(false);
        }
    };

    const updateInvoiceStatus = async (invoice, status, options = {}) => {
        if (!invoice) {
            return;
        }

        const statusLabel = String(status || '').toUpperCase();
        setInvoiceActionError('');
        setInvoiceActionMessage('');

        if (statusLabel === 'SENT' && !window.confirm(`Mark invoice ${invoice.id} as sent? It will become read-only.`)) {
            return;
        }

        if (statusLabel === 'PAID' && !window.confirm(`Mark invoice ${invoice.id} as paid?`)) {
            return;
        }

        if (statusLabel === 'DRAFT' && !options.force_unlock) {
            const confirmUnlock = window.confirm(`Unlock invoice ${invoice.id} and return it to draft?`);
            if (!confirmUnlock) {
                return;
            }
        }

        let reason = options.reason || '';
        if (options.force_unlock && !reason) {
            reason = window.prompt('Enter unlock reason');
            if (!reason || !String(reason).trim()) {
                return;
            }
        }

        setInvoiceActionBusy(invoice.id);
        setInvoiceActionError('');

        try {
            const response = await axios.patch(
                `${rootUrl}/api/owners/${encodeURIComponent(invoice.owner_code)}/transactions/${encodeURIComponent(invoice.id)}/monthly-invoice/status`,
                {
                    status: statusLabel,
                    force_unlock: !!options.force_unlock,
                    reason
                },
                {
                    headers: { Authorization: `Bearer ${token}` }
                }
            );

            if (response.data?.success) {
                setInvoiceActionMessage(`Invoice ${invoice.id} marked as ${statusLabel.toLowerCase()}.`);
                await refreshInvoiceHistory();
            } else {
                setInvoiceActionError(response.data?.message || 'Failed to update invoice status');
            }
        } catch (err) {
            console.error('Error updating invoice status:', err);
            setInvoiceActionError(err.response?.data?.message || 'Failed to update invoice status');
        } finally {
            setInvoiceActionBusy('');
        }
    };

    const markInvoiceSent = (invoice) => updateInvoiceStatus(invoice, 'SENT');
    const markInvoicePaid = (invoice) => updateInvoiceStatus(invoice, 'PAID');
    const unlockInvoice = (invoice) => updateInvoiceStatus(invoice, 'DRAFT', { force_unlock: true });

    const draftInvoiceRows = useMemo(
        () => recentInvoices.filter((invoice) => (
            canManageMonthlyInvoices
            && normalizeInvoiceStatus(invoice) === 'DRAFT'
            && !invoice?.is_reversed
        )),
        [canManageMonthlyInvoices, recentInvoices]
    );

    const selectedDraftInvoiceSet = useMemo(
        () => new Set(selectedDraftInvoiceIds.map((id) => String(id || '').trim())),
        [selectedDraftInvoiceIds]
    );

    const selectedDraftInvoices = useMemo(
        () => draftInvoiceRows.filter((invoice) => selectedDraftInvoiceSet.has(getInvoiceSelectionKey(invoice))),
        [draftInvoiceRows, getInvoiceSelectionKey, selectedDraftInvoiceSet]
    );

    const allDraftInvoicesSelected = draftInvoiceRows.length > 0 && selectedDraftInvoices.length === draftInvoiceRows.length;

    const toggleDraftInvoiceSelection = useCallback((invoiceId) => {
        const normalizedInvoiceId = String(invoiceId || '').trim();
        if (!normalizedInvoiceId) {
            return;
        }

        setSelectedDraftInvoiceIds((previous) => (
            previous.some((id) => String(id || '').trim() === normalizedInvoiceId)
                ? previous.filter((id) => String(id || '').trim() !== normalizedInvoiceId)
                : [...previous, normalizedInvoiceId]
        ));
    }, []);

    const toggleSelectAllDraftInvoices = useCallback(() => {
        if (draftInvoiceRows.length === 0) {
            return;
        }

        setSelectedDraftInvoiceIds(
            allDraftInvoicesSelected
                ? []
                : draftInvoiceRows.map((invoice) => getInvoiceSelectionKey(invoice)).filter(Boolean)
        );
    }, [allDraftInvoicesSelected, draftInvoiceRows, getInvoiceSelectionKey]);

    const markSelectedDraftInvoicesSent = useCallback(async () => {
        const invoicesToSend = selectedDraftInvoices.filter((invoice) => normalizeInvoiceStatus(invoice) === 'DRAFT');

        if (invoicesToSend.length === 0) {
            setInvoiceActionError('Select at least one draft invoice to mark as sent.');
            setInvoiceActionMessage('');
            return false;
        }

        if (!window.confirm(`Mark ${invoicesToSend.length} selected draft invoice(s) as sent? They will become read-only.`)) {
            return false;
        }

        setBulkSendingDraftInvoices(true);
        setInvoiceActionError('');
        setInvoiceActionMessage('');

        try {
            const items = invoicesToSend.map((invoice) => ({
                invoice_ref: invoice.id,
                owner_code: invoice.owner_code
            }));

            const response = await axios.post(
                `${rootUrl}/api/owners/transactions/monthly-invoice/status/bulk`,
                {
                    items,
                    status: 'SENT'
                },
                {
                    headers: { Authorization: `Bearer ${token}` }
                }
            );

            if (!response.data?.success) {
                setInvoiceActionError(response.data?.message || 'Failed to update invoice statuses');
                return false;
            }

            const results = Array.isArray(response.data?.data) ? response.data.data : [];
            const summaryResults = results.length > 0 ? results : items.map(() => ({ success: true }));
            const failedIndexes = [];

            summaryResults.forEach((result, index) => {
                if (!result?.success) {
                    failedIndexes.push(index);
                }
            });

            const successCount = summaryResults.filter((result) => result?.success).length;
            const failureCount = summaryResults.length - successCount;
            const failureMessages = summaryResults
                .map((result, index) => {
                    if (result?.success) {
                        return null;
                    }

                    const invoiceRef = items[index]?.invoice_ref || selectedDraftInvoices[index]?.id || `#${index + 1}`;
                    return `${invoiceRef}: ${result?.message || 'Failed to update'}`;
                })
                .filter(Boolean);

            setInvoiceActionMessage(
                failureCount > 0
                    ? `${successCount} invoice(s) marked as sent.`
                    : `${successCount} invoice(s) marked as sent successfully.`
            );
            setInvoiceActionError(
                failureCount > 0
                    ? `Could not update ${failureCount} invoice(s): ${failureMessages.slice(0, 3).join(' | ')}${failureMessages.length > 3 ? ' ...' : ''}`
                    : ''
            );

            const failedInvoiceIds = new Set(
                failedIndexes.map((index) => String(items[index]?.invoice_ref || '').trim()).filter(Boolean)
            );

            setSelectedDraftInvoiceIds(
                failedInvoiceIds.size > 0
                    ? selectedDraftInvoiceIds.filter((invoiceId) => failedInvoiceIds.has(String(invoiceId || '').trim()))
                    : []
            );

            await refreshInvoiceHistory();
            return true;
        } catch (err) {
            console.error('Error bulk updating invoice statuses:', err);
            setInvoiceActionError(getApiErrorMessage(err, 'Failed to update invoice statuses'));
            setInvoiceActionMessage('');
            return false;
        } finally {
            setBulkSendingDraftInvoices(false);
        }
    }, [getApiErrorMessage, refreshInvoiceHistory, selectedDraftInvoiceIds, selectedDraftInvoices, token]);

    const handlePostResultPreview = useCallback(() => {
        if (!postResult) {
            return;
        }

        openInvoicePreview({
            invoice_ref: postResult.invoice_ref,
            owner_code: postResult.owner_code,
            owner_name: postResult.owner_name,
            date: postResult.invoice_date,
            amount: postResult.total_amount || postResult.amount,
            gross_amount: postResult.gross_amount,
            discount_amount: postResult.discount_amount,
            car_count: postResult.car_count,
            description: `Monthly invoice for ${postResult.car_count || 0} line items`,
            notes,
            detail_lines: postResult.details || []
        });
    }, [notes, openInvoicePreview, postResult]);

    // Load recent invoices when selected owner changes
    useEffect(() => {
        if (selectedOwner && activeTab === 'history') {
            fetchRecentInvoices(selectedOwner.value);
        }
    }, [selectedOwner, activeTab, fetchRecentInvoices]);

    useEffect(() => {
        setSelectedDraftInvoiceIds([]);
        setInvoiceActionError('');
        setInvoiceActionMessage('');
    }, [selectedOwner?.value]);

    useEffect(() => {
        if (!previewError || activeTab !== 'create' || !previewErrorSectionRef.current) {
            return;
        }

        previewErrorSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        previewErrorSectionRef.current.focus({ preventScroll: true });
    }, [activeTab, previewError]);

    return (
        <div className="min-h-screen bg-gradient-to-br from-[#fff8f5] to-[#EDE4DA] font-sans text-xs">
            <div className="p-6 lg:p-8">
            <div className="text-center mb-8">
                    <div className="flex flex-col items-center gap-3">
                        <div>
                            <h1 className="text-4xl font-bold text-[#2a2e30] mb-2">Monthly Owner Invoices</h1>
                            <div className="w-24 h-1 bg-[#FF5733] mx-auto rounded-full mb-4"></div>
                            <p className="text-lg text-[#2a2e30] opacity-80">
                                Preview, edit, and post monthly invoices per owner
                            </p>
                        </div>
                        <Link
                            to="/owners/invoices/monthly/guide"
                            className="inline-flex items-center gap-2 rounded-full border border-[#E7D6CA] bg-white px-4 py-2 text-xs font-semibold text-[#2a2e30] shadow-sm hover:border-[#FF5733] hover:text-[#FF5733]"
                        >
                            <FaQuestionCircle />
                            Open Monthly Invoice Guide
                        </Link>
                        <Link
                            to="/owners/invoices/monthly/reports"
                            className="inline-flex items-center gap-2 rounded-full border border-[#E7D6CA] bg-white px-4 py-2 text-xs font-semibold text-[#2a2e30] shadow-sm hover:border-[#FF5733] hover:text-[#FF5733]"
                        >
                            <FaChartBar />
                            Open Monthly Reports
                        </Link>
                        <Link
                            to="/owners/invoices/monthly/bulk"
                            className="inline-flex items-center gap-2 rounded-full border border-[#E7D6CA] bg-white px-4 py-2 text-xs font-semibold text-[#2a2e30] shadow-sm hover:border-[#FF5733] hover:text-[#FF5733]"
                        >
                            <FaFileInvoiceDollar />
                            Bulk by Cars
                        </Link>
                    </div>
                </div>
            </div>

            <div className="w-full max-w-7xl mx-auto px-2 sm:px-4 pb-8">
                <MonthlyInvoicesHeader
                    activeTab={activeTab}
                    monthlySettings={monthlySettings}
                    onChangeTab={setActiveTab}
                />

                <Card className="border-0 shadow-md mb-6">
                    <CardBody className="p-4">
                        {/* Create Invoice Tab */}
                        {activeTab === 'create' && (
                            <div>
                                <Typography variant="h6" className="font-semibold text-gray-800 mb-4">
                                    Preview Monthly Invoice
                                </Typography>

                                <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 items-end mb-4">
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                                            Owner
                                        </label>
                                        <ReactSelect
                                            value={selectedOwner}
                                            onChange={(option) => {
                                                setSelectedOwner(option);
                                                if (errors.owner) {
                                                    setErrors(prev => ({ ...prev, owner: '' }));
                                                }
                                            }}
                                            options={ownerOptions}
                                            placeholder="Select owner"
                                            styles={selectStyles}
                                        />
                                        {errors.owner && <p className="text-red-500 text-sm mt-1">{errors.owner}</p>}
                                    </div>
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                                            Year
                                        </label>
                                        <ReactSelect
                                            value={selectedYear}
                                            onChange={(option) => {
                                                setSelectedYear(option);
                                                if (errors.year) {
                                                    setErrors(prev => ({ ...prev, year: '' }));
                                                }
                                            }}
                                            options={yearOptions}
                                            placeholder="Select year"
                                            styles={selectStyles}
                                        />
                                        {errors.year && <p className="text-red-500 text-sm mt-1">{errors.year}</p>}
                                    </div>
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                                            Month
                                        </label>
                                        <ReactSelect
                                            value={selectedMonth}
                                            onChange={(option) => {
                                                setSelectedMonth(option);
                                                if (errors.month) {
                                                    setErrors(prev => ({ ...prev, month: '' }));
                                                }
                                            }}
                                            options={monthOptions}
                                            placeholder="Select month"
                                            styles={selectStyles}
                                        />
                                        {errors.month && <p className="text-red-500 text-sm mt-1">{errors.month}</p>}
                                    </div>
                                    <div>
                                        <Button
                                            onClick={handlePreview}
                                            disabled={previewLoading}
                                            className="w-full bg-[#FF5733] hover:bg-[#e64520] text-white text-xs font-semibold rounded-sm px-4 py-2 shadow-none inline-flex items-center justify-center gap-2"
                                        >
                                            <FaEye className="text-xs" />
                                            {previewLoading ? 'Loading Preview...' : 'Preview Invoice'}
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Invoice History Tab */}
                        {activeTab === 'history' && (
                            <div>
                                <Typography variant="h6" className="font-semibold text-gray-800 mb-4">
                                    Recent Monthly Invoices
                                    {selectedOwner && (
                                        <span className="text-gray-600 font-normal ml-2">
                                            for {selectedOwner.label}
                                        </span>
                                    )}
                                </Typography>

                                {!selectedOwner && (
                                    <div className="bg-yellow-50 border border-yellow-200 rounded p-3 mb-4">
                                        <Typography className="text-yellow-800 text-sm">
                                            Please select an owner from the dropdown below to view invoice history.
                                        </Typography>
                                    </div>
                                )}

                                {selectedOwner && (
                                    <div className="mb-4">
                                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                                            Owner
                                        </label>
                                        <ReactSelect
                                            value={selectedOwner}
                                            onChange={(option) => {
                                                setSelectedOwner(option);
                                            }}
                                            options={ownerOptions}
                                            placeholder="Select owner"
                                            styles={selectStyles}
                                        />
                                    </div>
                                )}

                                {selectedOwner && invoicesLoading && (
                                    <div className="text-center py-8">
                                        <Typography className="text-gray-600">Loading invoices...</Typography>
                                    </div>
                                )}

                                {selectedOwner && !invoicesLoading && recentInvoices.length === 0 && (
                                    <div className="bg-blue-50 border border-blue-200 rounded p-3">
                                        <Typography className="text-blue-800 text-sm">
                                            No monthly invoices found for this owner.
                                        </Typography>
                                    </div>
                                )}

                                {selectedOwner && !invoicesLoading && recentInvoices.length > 0 && (
                                    <>
                                        {invoiceActionError && (
                                            <div className="mb-4 rounded border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">
                                                {invoiceActionError}
                                            </div>
                                        )}

                                        {invoiceActionMessage && (
                                            <div className="mb-4 rounded border border-green-200 bg-green-50 px-4 py-3 text-sm font-semibold text-green-700">
                                                {invoiceActionMessage}
                                            </div>
                                        )}

                                        {canManageMonthlyInvoices && draftInvoiceRows.length > 0 && (
                                            <div className="mb-4 flex flex-col gap-3 rounded-xl border border-dashed border-slate-300 bg-white/90 px-4 py-3 lg:flex-row lg:items-center lg:justify-between">
                                                <div className="flex items-center gap-3 text-sm text-slate-700">
                                                    <label className="inline-flex items-center gap-2 rounded-full bg-slate-50 px-3 py-2 font-semibold text-slate-700">
                                                        <input
                                                            type="checkbox"
                                                            checked={allDraftInvoicesSelected}
                                                            onChange={toggleSelectAllDraftInvoices}
                                                            className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                                                            aria-label={allDraftInvoicesSelected ? 'Uncheck all draft invoices' : 'Check all draft invoices'}
                                                        />
                                                        Check all
                                                    </label>
                                                    <span>
                                                        <span className="font-semibold text-slate-900">Draft invoices</span>
                                                        <span className="ml-2">{selectedDraftInvoices.length} selected of {draftInvoiceRows.length}</span>
                                                    </span>
                                                </div>
                                                <div className="flex flex-wrap gap-2">
                                                    <Button
                                                        onClick={toggleSelectAllDraftInvoices}
                                                        disabled={draftInvoiceRows.length === 0}
                                                        variant="outlined"
                                                        className="border-slate-300 text-slate-700"
                                                    >
                                                        {allDraftInvoicesSelected ? 'Clear Draft Selection' : 'Select All Drafts'}
                                                    </Button>
                                                    <button
                                                        type="button"
                                                        onClick={markSelectedDraftInvoicesSent}
                                                        disabled={bulkSendingDraftInvoices || selectedDraftInvoices.length === 0}
                                                        className="inline-flex items-center gap-2 rounded-sm bg-[#FF5733] px-4 py-2 text-sm font-semibold text-white shadow-none transition-colors duration-200 hover:bg-[#e64520] disabled:cursor-not-allowed disabled:opacity-50"
                                                    >
                                                        <FaPaperPlane />
                                                        {bulkSendingDraftInvoices ? 'Updating...' : 'Mark Selected Drafts as Sent'}
                                                    </button>
                                                </div>
                                            </div>
                                        )}

                                        <div className="grid grid-cols-1 gap-3 md:hidden">
                                            {recentInvoices.map((invoice) => (
                                                <div key={invoice.id} className={`rounded-xl border p-4 shadow-sm ${invoice.is_reversed ? 'bg-gray-100 border-gray-200' : 'bg-white border-gray-200'}`}>
                                                    <div className="flex items-start justify-between gap-3">
                                                        <div className="min-w-0">
                                                            <Link
                                                                to={buildOwnerTransactionPath(invoice.owner_code, invoice.id)}
                                                                className="font-mono text-xs text-[#FF5733] break-all hover:text-[#e64520] hover:underline"
                                                            >
                                                                {getDocumentNumber(invoice.id)}
                                                            </Link>
                                                            <p className="mt-1 text-sm font-semibold text-gray-800">
                                                                {invoice.date
                                                                    ? new Date(invoice.date).toLocaleDateString('en-US', {
                                                                        month: 'short',
                                                                        day: 'numeric',
                                                                        year: 'numeric'
                                                                    })
                                                                    : '-'}
                                                            </p>
                                                        </div>
                                                        <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${getInvoiceStatusMeta(invoice).className}`}>
                                                            {getInvoiceStatusMeta(invoice).label}
                                                        </span>
                                                    </div>

                                                    {canManageMonthlyInvoices && normalizeInvoiceStatus(invoice) === 'DRAFT' && !invoice.is_reversed && (
                                                        <label className="mt-3 inline-flex items-center gap-2 rounded-full bg-slate-50 px-3 py-2 text-xs font-semibold text-slate-700">
                                                            <input
                                                                type="checkbox"
                                                                checked={selectedDraftInvoiceSet.has(getInvoiceSelectionKey(invoice))}
                                                                onChange={() => toggleDraftInvoiceSelection(invoice.id)}
                                                                className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                                                            />
                                                            Select for bulk send
                                                        </label>
                                                    )}

                                                    <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
                                                        <div>
                                                            <p className="text-xs uppercase tracking-wide text-gray-500">Line Items</p>
                                                            <p className="font-semibold text-gray-800">{invoice.car_count}</p>
                                                        </div>
                                                        <div>
                                                            <p className="text-xs uppercase tracking-wide text-gray-500">Posted By</p>
                                                            <p className="font-semibold text-gray-800 break-words">{invoice.input_by || '-'}</p>
                                                        </div>
                                                        <div>
                                                            <p className="text-xs uppercase tracking-wide text-gray-500">Gross</p>
                                                            <p className="font-semibold text-gray-800">{formatMoney(invoice.gross_amount || invoice.amount || 0)}</p>
                                                        </div>
                                                        <div>
                                                            <p className="text-xs uppercase tracking-wide text-gray-500">Discount</p>
                                                            <p className="font-semibold text-red-700">{formatMoney(invoice.discount_amount || 0)}</p>
                                                        </div>
                                                        <div className="col-span-2">
                                                            <p className="text-xs uppercase tracking-wide text-gray-500">Net (KES)</p>
                                                            <p className="font-semibold text-gray-800">{formatMoney(invoice.amount || 0)}</p>
                                                        </div>
                                                    </div>

                                                    <div className="mt-4 flex items-center gap-2">
                                                        <button
                                                            type="button"
                                                            onClick={() => openInvoicePreview(invoice)}
                                                            className={actionIconPreviewClass}
                                                            aria-label="Preview invoice"
                                                            title="Preview invoice"
                                                        >
                                                            <FaEye className="text-xs" />
                                                        </button>
                                                        {canManageMonthlyInvoices && isInvoiceEditable(invoice) && (
                                                            <button
                                                                type="button"
                                                                onClick={() => openEditInvoiceDialog(invoice)}
                                                                className={actionIconEditClass}
                                                                aria-label="Edit invoice"
                                                                title="Edit draft invoice"
                                                            >
                                                                <FaEdit className="text-xs" />
                                                            </button>
                                                        )}
                                                        {canManageMonthlyInvoices && normalizeInvoiceStatus(invoice) === 'DRAFT' && (
                                                            <button
                                                                type="button"
                                                                onClick={() => markInvoiceSent(invoice)}
                                                                disabled={invoiceActionBusy === invoice.id}
                                                                className={actionIconSentClass}
                                                                aria-label="Mark sent"
                                                                title="Mark as sent"
                                                            >
                                                                <FaLock className="text-xs" />
                                                            </button>
                                                        )}
                                                        {canManageMonthlyInvoices && normalizeInvoiceStatus(invoice) === 'SENT' && (
                                                            <button
                                                                type="button"
                                                                onClick={() => markInvoicePaid(invoice)}
                                                                disabled={invoiceActionBusy === invoice.id}
                                                                className={actionIconPaidClass}
                                                                aria-label="Mark paid"
                                                                title="Mark as paid"
                                                            >
                                                                <FaCheck className="text-xs" />
                                                            </button>
                                                        )}
                                                        {canManageMonthlyInvoices && normalizeInvoiceStatus(invoice) === 'SENT' && (
                                                            <button
                                                                type="button"
                                                                onClick={() => unlockInvoice(invoice)}
                                                                disabled={invoiceActionBusy === invoice.id}
                                                                className={actionIconUnlockClass}
                                                                aria-label="Unlock invoice"
                                                                title="Unlock draft (admin override)"
                                                            >
                                                                <FaUnlock className="text-xs" />
                                                            </button>
                                                        )}
                                                        <button
                                                            type="button"
                                                            onClick={() => handleReverseInvoice(invoice.id)}
                                                            disabled={reversingInvoiceId === invoice.id || invoice.is_reversed}
                                                            title={invoice.is_reversed ? 'This invoice has already been reversed' : 'Reverse this invoice'}
                                                            aria-label="Reverse invoice"
                                                            className={`${actionIconReverseClass} ${invoice.is_reversed ? 'bg-gray-200 text-gray-500 opacity-50' : ''}`}
                                                        >
                                                            <FaUndoAlt className="text-xs" />
                                                        </button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>

                                        <div className="hidden overflow-x-auto md:block">
                                        <table className="min-w-[960px] w-full text-sm">
                                            <thead>
                                                <tr className="border-b border-gray-300 bg-gray-50">
                                                    {canManageMonthlyInvoices && (
                                                        <th className="px-4 py-3 text-center font-semibold text-gray-700">Select</th>
                                                    )}
                                                    <th className="px-4 py-3 text-left font-semibold text-gray-700">Invoice Ref</th>
                                                    <th className="px-4 py-3 text-center font-semibold text-gray-700">Date</th>
                                                    <th className="px-4 py-3 text-center font-semibold text-gray-700">Line Items</th>
                                                    <th className="px-4 py-3 text-right font-semibold text-gray-700">Gross</th>
                                                    <th className="px-4 py-3 text-right font-semibold text-gray-700">Discount</th>
                                                    <th className="px-4 py-3 text-right font-semibold text-gray-700">Net (KES)</th>
                                                    <th className="px-4 py-3 text-left font-semibold text-gray-700">Posted By</th>
                                                    <th className="px-4 py-3 text-center font-semibold text-gray-700">Status</th>
                                                    <th className="px-4 py-3 text-center font-semibold text-gray-700">Action</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {recentInvoices.map((invoice) => (
                                                <tr key={invoice.id} className={`border-b border-gray-200 ${invoice.is_reversed ? 'bg-gray-100' : 'hover:bg-gray-50'}`}>
                                                        {canManageMonthlyInvoices && (
                                                            <td className="px-4 py-3 text-center">
                                                                {normalizeInvoiceStatus(invoice) === 'DRAFT' && !invoice.is_reversed ? (
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={selectedDraftInvoiceSet.has(getInvoiceSelectionKey(invoice))}
                                                                        onChange={() => toggleDraftInvoiceSelection(invoice.id)}
                                                                        className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                                                                    />
                                                                ) : (
                                                                    <span className="text-xs text-gray-400">-</span>
                                                                )}
                                                            </td>
                                                        )}
                                                        <td className="px-4 py-3 font-mono text-xs text-gray-800">
                                                            <Link
                                                                to={buildOwnerTransactionPath(invoice.owner_code, invoice.id)}
                                                                className="text-[#FF5733] hover:text-[#e64520] hover:underline"
                                                            >
                                                                {getDocumentNumber(invoice.id)}
                                                            </Link>
                                                        </td>
                                                        <td className="px-4 py-3 text-center text-gray-600">
                                                            {invoice.date
                                                                ? new Date(invoice.date).toLocaleDateString('en-US', {
                                                                    month: 'short',
                                                                    day: 'numeric',
                                                                    year: 'numeric'
                                                                })
                                                                : '-'}
                                                        </td>
                                                        <td className="px-4 py-3 text-center text-gray-600">
                                                            {invoice.car_count}
                                                        </td>
                                                        <td className="px-4 py-3 text-right font-semibold text-gray-800">
                                                            {formatMoney(invoice.gross_amount || invoice.amount || 0)}
                                                        </td>
                                                        <td className="px-4 py-3 text-right font-semibold text-red-700">
                                                            {formatMoney(invoice.discount_amount || 0)}
                                                        </td>
                                                        <td className="px-4 py-3 text-right font-semibold text-gray-800">
                                                            {formatMoney(invoice.amount || 0)}
                                                        </td>
                                                        <td className="px-4 py-3 text-gray-600 text-xs">
                                                            {invoice.input_by}
                                                        </td>
                                                        <td className="px-4 py-3 text-center">
                                                            <span className={`inline-block px-3 py-1 text-xs font-semibold rounded-full ${getInvoiceStatusMeta(invoice).className}`}>
                                                                {getInvoiceStatusMeta(invoice).label}
                                                            </span>
                                                        </td>
                                                        <td className="px-4 py-3 text-center">
                                                            <div className="flex flex-wrap items-center justify-center gap-2">
                                                        <button
                                                            type="button"
                                                            onClick={() => openInvoicePreview(invoice)}
                                                            className={actionIconPreviewClass}
                                                            aria-label="Preview invoice"
                                                            title="Preview invoice"
                                                                    >
                                                                        <FaEye className="text-xs" />
                                                                    </button>
                                                                {canManageMonthlyInvoices && isInvoiceEditable(invoice) && (
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => openEditInvoiceDialog(invoice)}
                                                                        className={actionIconEditClass}
                                                                        aria-label="Edit invoice"
                                                                        title="Edit draft invoice"
                                                                    >
                                                                        <FaEdit className="text-xs" />
                                                                    </button>
                                                                )}
                                                                {canManageMonthlyInvoices && normalizeInvoiceStatus(invoice) === 'DRAFT' && (
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => markInvoiceSent(invoice)}
                                                                        disabled={invoiceActionBusy === invoice.id}
                                                                        className={actionIconSentClass}
                                                                        aria-label="Mark sent"
                                                                        title="Mark as sent"
                                                                    >
                                                                        <FaLock className="text-xs" />
                                                                    </button>
                                                                )}
                                                                {canManageMonthlyInvoices && normalizeInvoiceStatus(invoice) === 'SENT' && (
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => markInvoicePaid(invoice)}
                                                                        disabled={invoiceActionBusy === invoice.id}
                                                                        className={actionIconPaidClass}
                                                                        aria-label="Mark paid"
                                                                        title="Mark as paid"
                                                                    >
                                                                        <FaCheck className="text-xs" />
                                                                    </button>
                                                                )}
                                                                {canManageMonthlyInvoices && normalizeInvoiceStatus(invoice) === 'SENT' && (
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => unlockInvoice(invoice)}
                                                                        disabled={invoiceActionBusy === invoice.id}
                                                                        className={actionIconUnlockClass}
                                                                        aria-label="Unlock invoice"
                                                                        title="Unlock draft (admin override)"
                                                                    >
                                                                        <FaUnlock className="text-xs" />
                                                                    </button>
                                                                )}
                                                                <button
                                                                    type="button"
                                                                    onClick={() => handleReverseInvoice(invoice.id)}
                                                                    disabled={reversingInvoiceId === invoice.id || invoice.is_reversed}
                                                                    title={invoice.is_reversed ? 'This invoice has already been reversed' : 'Reverse this invoice'}
                                                                    aria-label="Reverse invoice"
                                                                    className={`${actionIconReverseClass} ${invoice.is_reversed ? 'bg-gray-200 text-gray-500 opacity-50' : ''}`}
                                                                >
                                                                    <FaUndoAlt className="text-xs" />
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                    </>
                                )}

                                {reverseError && (
                                    <div className="mt-4 bg-red-50 border border-red-200 rounded p-3">
                                        <Typography variant="small" className="text-red-700 font-semibold">
                                            {reverseError}
                                        </Typography>
                                    </div>
                                )}

                                {reverseSuccess && (
                                    <div className="mt-4 bg-green-50 border border-green-200 rounded p-3">
                                        <Typography variant="small" className="font-semibold text-green-700 mb-2">
                                            Invoice Reversed Successfully
                                        </Typography>
                                        <div className="text-sm text-green-700">
                                            <p>Original: <span className="font-mono">{reverseSuccess.original_invoice}</span></p>
                                            <p>Reversal: <span className="font-mono">{reverseSuccess.reversal_ref}</span></p>
                                            <p>Amount: {formatMoney(reverseSuccess.amount || 0)}</p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </CardBody>
                </Card>

                <Dialog open={editDialogOpen} handler={closeEditInvoiceDialog} size="md">
                    <DialogHeader>Edit Draft Invoice</DialogHeader>
                    <DialogBody className="space-y-4">
                        <div>
                            <label className="mb-2 block text-sm font-semibold text-gray-700">Invoice Date</label>
                            <Input
                                type="date"
                                value={editingInvoiceDate}
                                onChange={(e) => setEditingInvoiceDate(e.target.value)}
                                className="border border-gray-300 rounded"
                            />
                        </div>
                        <div>
                            <label className="mb-2 block text-sm font-semibold text-gray-700">Notes</label>
                            <textarea
                                value={editingInvoiceNotes}
                                onChange={(e) => setEditingInvoiceNotes(e.target.value)}
                                rows="4"
                                className="w-full rounded border border-gray-300 px-3 py-2 text-sm text-gray-700 outline-none focus:border-[#FF5733]"
                            />
                        </div>
                        <div className="rounded border border-yellow-200 bg-yellow-50 px-3 py-2 text-xs text-yellow-800">
                            Only draft invoices can be edited. Once marked sent, the invoice becomes read-only until a super-admin unlocks it.
                        </div>
                    </DialogBody>
                    <DialogFooter className="gap-2">
                        <Button variant="text" onClick={closeEditInvoiceDialog} className="text-gray-700">
                            Cancel
                        </Button>
                        <Button
                            onClick={saveEditedInvoice}
                            disabled={editingInvoiceBusy}
                            className="bg-[#FF5733] text-white shadow-none"
                        >
                            {editingInvoiceBusy ? 'Saving...' : 'Save Changes'}
                        </Button>
                    </DialogFooter>
                </Dialog>

                {activeTab === 'create' && (
                    <div>
                        {previewError && (
                            <Card ref={previewErrorSectionRef} tabIndex={-1} className="border-0 shadow-md border-l-4 border-l-red-500 mb-6 outline-none">
                                <CardBody className="p-4">
                                    <Typography variant="small" className="font-semibold text-red-700">
                                        {previewError}
                                    </Typography>
                                    {previewErrorLink?.to && (
                                        <div className="mt-2 space-y-1">
                                            {previewErrorLink.invoiceNumber && (
                                                <Typography className="text-sm font-semibold text-gray-700">
                                                    Existing invoice #{previewErrorLink.invoiceNumber}
                                                </Typography>
                                            )}
                                            <Link
                                                to={previewErrorLink.to}
                                                className="text-sm font-semibold text-[#FF5733] hover:text-[#e64520] hover:underline"
                                            >
                                                {previewErrorLink.label}
                                            </Link>
                                        </div>
                                    )}
                                </CardBody>
                            </Card>
                        )}

                        {previewData && (
                            <Card className="border-0 shadow-md mb-6">
                                <CardBody className="p-4">
                                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-4">
                                        <div>
                                            <Typography variant="h6" className="font-semibold text-gray-800">
                                                Preview for {previewData.owner_code} - {previewData.owner_name}
                                            </Typography>
                                            <Typography className="text-sm text-gray-600 mt-1">
                                                {new Date(previewData.year, previewData.month - 1).toLocaleDateString('en-US', {
                                                    month: 'long',
                                                    year: 'numeric'
                                                })}
                                            </Typography>
                                        </div>
                                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 w-full lg:max-w-2xl">
                                            <div className="rounded border border-green-200 bg-green-50 p-3">
                                                <Typography className="text-xs font-semibold uppercase tracking-wide text-green-700">
                                                    Net Amount
                                                </Typography>
                                                <Typography className="text-lg font-semibold text-green-800">
                                                    {formatMoney(previewTotals.totalAmount)}
                                                </Typography>
                                                <Typography className="text-xs text-gray-500">
                                                    Line Items: {previewTotals.carCount}
                                                </Typography>
                                            </div>
                                            <div className="rounded border border-amber-200 bg-amber-50 p-3">
                                                <Typography className="text-xs font-semibold uppercase tracking-wide text-amber-700">
                                                    Gross Amount
                                                </Typography>
                                                <Typography className="text-lg font-semibold text-amber-800">
                                                    {formatMoney(previewTotals.totalGrossAmount)}
                                                </Typography>
                                                <Typography className="text-xs text-gray-500">
                                                    Before discounts
                                                </Typography>
                                            </div>
                                            <div className="rounded border border-red-200 bg-red-50 p-3">
                                                <Typography className="text-xs font-semibold uppercase tracking-wide text-red-700">
                                                    Discount Amount
                                                </Typography>
                                                <Typography className="text-lg font-semibold text-red-800">
                                                    {formatMoney(previewTotals.totalDiscountAmount)}
                                                </Typography>
                                                <Typography className="text-xs text-gray-500">
                                                    Deducted from gross
                                                </Typography>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
                                        <div>
                                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                                Invoice Date
                                            </label>
                                                <Input
                                                    type="date"
                                                    value={invoiceDate}
                                                    onChange={(e) => setInvoiceDate(e.target.value)}
                                                    className={`${compactInputClass} w-full border border-gray-300 rounded`}
                                                />
                                        </div>
                                        <div className="lg:col-span-2">
                                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                                Notes
                                            </label>
                                            <textarea
                                                value={notes}
                                                onChange={(e) => setNotes(e.target.value)}
                                                className="w-full border border-gray-300 rounded px-3 py-2 text-sm text-gray-700"
                                                rows="2"
                                            />
                                        </div>
                                    </div>

                                    <div className="mb-4 grid grid-cols-1 gap-3 md:hidden">
                                        {previewRows.map((row) => (
                                            <div key={row.id} className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                                                <div className="flex items-start justify-between gap-3">
                                                    <div className="min-w-0">
                                                        <label className="flex items-center gap-2 text-sm font-semibold text-gray-800">
                                                            <input
                                                                type="checkbox"
                                                                checked={row.include === true}
                                                                onChange={(e) => updatePreviewRow(row.id, { include: e.target.checked })}
                                                                aria-label={`Include ${row.plate_no}`}
                                                            />
                                                            {renderPlateLabel(row)}
                                                        </label>
                                                        <p className="mt-1 text-xs text-gray-500">
                                                            {row.allocation_date
                                                                ? new Date(row.allocation_date).toLocaleDateString('en-US', {
                                                                    month: 'short',
                                                                    day: 'numeric',
                                                                    year: 'numeric'
                                                                })
                                                                : '-'}
                                                        </p>
                                                    </div>
                                                        <button
                                                            onClick={() => navigate(`/cars/${encodeURIComponent(row.plate_no)}`)}
                                                            className="rounded-md bg-[#FFF3EE] px-3 py-1 text-xs font-semibold text-[#FF5733]"
                                                            aria-label={`Open vehicle ${row.plate_no}`}
                                                        >
                                                        Open
                                                    </button>
                                                </div>

                                                <div className="mt-3 grid grid-cols-1 gap-3 text-sm">
                                                    <div className="min-w-0">
                                                        <p className="text-xs uppercase tracking-wide text-gray-500">Base</p>
                                                        <Input
                                                            {...compactNumberInputProps}
                                                            containerProps={{ className: 'w-full min-w-0' }}
                                                            className="!w-full"
                                                            value={row.base_amount}
                                                            onChange={(e) => updatePreviewRow(row.id, { base_amount: e.target.value })}
                                                        />
                                                    </div>
                                                    <div className="min-w-0">
                                                        <p className="text-xs uppercase tracking-wide text-gray-500">Days</p>
                                                        <Input
                                                            type="number"
                                                            step="1"
                                                            containerProps={{ className: 'w-full min-w-0' }}
                                                            className="!w-full text-right"
                                                            value={row.days_counted}
                                                            onChange={(e) => updatePreviewRow(row.id, { days_counted: e.target.value })}
                                                        />
                                                    </div>
                                                    <div className="min-w-0">
                                                        <p className="text-xs uppercase tracking-wide text-gray-500">Gross</p>
                                                        <p className="font-semibold text-gray-800">{formatMoney(row.gross_amount || 0)}</p>
                                                    </div>
                                                    <div className="min-w-0">
                                                        <p className="text-xs uppercase tracking-wide text-gray-500">Discount</p>
                                                        <Input
                                                            {...compactNumberInputProps}
                                                            containerProps={{ className: 'w-full min-w-0' }}
                                                            className="!w-full"
                                                            value={row.discount_amount}
                                                            onChange={(e) => updatePreviewRow(row.id, { discount_amount: e.target.value })}
                                                        />
                                                    </div>
                                                    <div className="min-w-0">
                                                        <p className="text-xs uppercase tracking-wide text-gray-500">Net Amount</p>
                                                        <p className="font-semibold text-gray-800">{formatMoney(row.prorated_amount || 0)}</p>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                    <div className="overflow-x-auto mb-4 hidden md:block">
                                        <table className="min-w-[960px] w-full text-sm">
                                            <thead className="bg-gray-50 border-b border-gray-200">
                                                <tr>
                                                    <th className="px-3 py-2 text-center font-semibold text-gray-700">Include</th>
                                                    <th className="px-3 py-2 text-left font-semibold text-gray-700">Plate No</th>
                                                    <th className="px-3 py-2 text-left font-semibold text-gray-700">Allocation Date</th>
                                                    <th className="px-3 py-2 text-right font-semibold text-gray-700">Base Amount</th>
                                                    <th className="px-3 py-2 text-right font-semibold text-gray-700">Days Counted</th>
                                                    <th className="px-3 py-2 text-right font-semibold text-gray-700">Gross Amount</th>
                                                    <th className="px-3 py-2 text-right font-semibold text-gray-700">Discount</th>
                                                    <th className="px-3 py-2 text-right font-semibold text-gray-700">Net Amount</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {previewRows.map((row) => (
                                                    <tr key={row.id} className="border-t border-gray-200">
                                                        <td className="px-3 py-2 text-center">
                                                            <input
                                                                type="checkbox"
                                                                checked={row.include === true}
                                                                onChange={(e) => updatePreviewRow(row.id, { include: e.target.checked })}
                                                                aria-label={`Include ${row.plate_no}`}
                                                            />
                                                        </td>
                                                        <td className="px-3 py-2 font-semibold text-gray-800">
                                                            <button
                                                                onClick={() => navigate(`/cars/${encodeURIComponent(row.plate_no)}`)}
                                                                className="text-[#FF5733] underline inline-block"
                                                                aria-label={`Open vehicle ${row.plate_no}`}
                                                            >
                                                                {renderPlateLabel(row)}
                                                            </button>
                                                        </td>
                                                        <td className="px-3 py-2 text-gray-600 text-xs">
                                                            {row.allocation_date
                                                                ? new Date(row.allocation_date).toLocaleDateString('en-US', {
                                                                    month: 'short',
                                                                    day: 'numeric',
                                                                    year: 'numeric'
                                                                })
                                                                : '-'}
                                                        </td>
                                                        <td className="px-3 py-2 text-right">
                                                            <Input
                                                                {...compactNumberInputProps}
                                                                containerProps={{ className: compactAmountContainerClass }}
                                                                value={row.base_amount}
                                                                onChange={(e) => updatePreviewRow(row.id, { base_amount: e.target.value })}
                                                            />
                                                        </td>
                                                        <td className="px-3 py-2 text-right">
                                                            <Input
                                                                type="number"
                                                                step="1"
                                                                containerProps={{ className: compactDaysContainerClass }}
                                                                className={`${compactInputClass} text-right`}
                                                                value={row.days_counted}
                                                                onChange={(e) => updatePreviewRow(row.id, { days_counted: e.target.value })}
                                                            />
                                                        </td>
                                                        <td className="px-3 py-2 text-right text-gray-700">
                                                            {formatMoney(row.gross_amount || 0)}
                                                        </td>
                                                        <td className="px-3 py-2 text-right">
                                                            <Input
                                                                {...compactNumberInputProps}
                                                                containerProps={{ className: compactAmountContainerClass }}
                                                                value={row.discount_amount}
                                                                onChange={(e) => updatePreviewRow(row.id, { discount_amount: e.target.value })}
                                                            />
                                                        </td>
                                                        <td className="px-3 py-2 text-right font-semibold text-gray-800">
                                                            {formatMoney(row.prorated_amount || 0)}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>

                                    <div className="flex justify-end">
                <Button
                    onClick={handlePostInvoice}
                    disabled={postLoading || !previewData || previewRows.length === 0 || previewSignature !== currentPreviewSignature}
                    className="w-full sm:w-auto bg-[#FF5733] hover:bg-[#e64520] text-white text-xs font-semibold rounded-sm px-4 py-2 shadow-none inline-flex items-center justify-center gap-2"
                >
                                            <FaFileInvoiceDollar className="text-xs" />
                                            {postLoading ? 'Posting...' : 'Post Invoice'}
                                        </Button>
                                    </div>
                                </CardBody>
                            </Card>
                        )}

                        {postResult && (
                            <Card className="border-0 shadow-md">
                                <CardBody className="p-4">
                                    <Typography variant="h6" className="font-semibold text-gray-800 mb-2">
                                        Invoice Posted Successfully
                                    </Typography>
                                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm text-gray-700">
                                        <div>
                                            <p className="font-semibold">Owner</p>
                                            <p>{postResult.owner_code} - {postResult.owner_name}</p>
                                        </div>
                                        <div>
                                            <p className="font-semibold">Invoice Ref</p>
                                            <p className="font-mono text-xs">
                                                <Link
                                                    to={buildOwnerTransactionPath(postResult.owner_code, postResult.invoice_ref)}
                                                    className="text-[#FF5733] hover:text-[#e64520] hover:underline"
                                                >
                                                    {getDocumentNumber(postResult.invoice_ref)}
                                                </Link>
                                            </p>
                                        </div>
                                        <div className="text-right">
                                            <p className="font-semibold">Gross Amount</p>
                                            <p className="text-amber-700 font-semibold">{formatMoney(postResult.gross_amount || 0)}</p>
                                        </div>
                                        <div className="text-right">
                                            <p className="font-semibold">Discount Amount</p>
                                            <p className="text-red-700 font-semibold">{formatMoney(postResult.discount_amount || 0)}</p>
                                        </div>
                                        <div className="text-right md:col-span-4">
                                            <p className="font-semibold">Net Amount</p>
                                            <p className="text-green-700 font-semibold">{formatMoney(postResult.total_amount || 0)}</p>
                                        </div>
                                    </div>
                                    <div className="mt-3 text-xs text-gray-500">
                                        Posted on {postResult.invoice_date
                                            ? new Date(postResult.invoice_date).toLocaleDateString('en-US', {
                                                month: 'short',
                                                day: 'numeric',
                                                year: 'numeric'
                                            })
                                            : '-'}
                                    </div>
                                    <div className="mt-4 flex justify-end">
                                        <Button
                                            onClick={handlePostResultPreview}
                                            className="w-full sm:w-auto bg-[#FF5733] hover:bg-[#e64520] text-white text-xs font-semibold rounded-sm px-4 py-2 shadow-none inline-flex items-center justify-center gap-2"
                                        >
                                            <FaEye className="text-xs" />
                                            Preview Document
                                        </Button>
                                    </div>
                                </CardBody>
                            </Card>
                        )}

                        {!previewData && !previewError && (
                            <Card className="border-0 shadow-md bg-blue-50">
                                <CardBody className="p-6 text-center">
                                    <Typography className="text-gray-600">
                                        Select an owner, month, and year above, then click "Preview Invoice" to begin.
                                    </Typography>
                                </CardBody>
                            </Card>
                        )}
                    </div>
                )}

                {previewDialogOpen && previewTransaction && (
                    <OwnerTransactionPreviewDialog
                        open={previewDialogOpen}
                        onClose={closeInvoicePreview}
                        transaction={previewTransaction}
                    />
                )}

            </div>
        </div>
    );
};

export default MonthlyInvoices;
